import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  Select as AriaSelect,
  TextField as AriaTextField,
  FieldError,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SelectValue,
  Text,
  TextArea,
} from 'react-aria-components';
import { cn, FOCUS_RING } from './cn.ts';

/** `py-[7px]` rather than a scale step so a field is exactly `h-9` — the height
 *  of a `md` button, which is what it sits beside in every form row here. */
const CONTROL =
  'w-full rounded-md border border-primary bg-primary px-3 py-[7px] text-sm text-primary ' +
  'placeholder:text-placeholder transition-colors ' +
  'data-focused:border-brand data-focused:focus-ring ' +
  'data-invalid:border-error ' +
  'data-disabled:bg-disabled data-disabled:text-disabled data-disabled:cursor-not-allowed';

const LABEL = 'block text-sm font-medium text-secondary';
const HINT = 'block text-sm text-tertiary';
const ERROR = 'block text-sm text-error-primary';

export interface TextFieldProps {
  label: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  type?: 'text' | 'email' | 'search';
  placeholder?: string;
  description?: ReactNode;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  multiline?: boolean;
  rows?: number;
  className?: string;
}

export function TextField({
  label,
  description,
  errorMessage,
  multiline,
  rows = 3,
  className,
  onChange,
  ...props
}: TextFieldProps) {
  return (
    <AriaTextField
      {...props}
      onChange={onChange}
      isInvalid={errorMessage ? true : undefined}
      className={cn('flex flex-col gap-1.5', className)}
    >
      <Label className={LABEL}>
        {label}
        {props.isRequired ? <span className="text-error-primary"> *</span> : null}
      </Label>
      {multiline ? (
        <TextArea rows={rows} className={cn(CONTROL, 'resize-y')} />
      ) : (
        <Input className={CONTROL} />
      )}
      {description ? (
        <Text slot="description" className={HINT}>
          {description}
        </Text>
      ) : null}
      <FieldError className={ERROR}>{errorMessage}</FieldError>
    </AriaTextField>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  description?: string;
}

export interface SelectProps<T extends string> {
  label: string;
  options: ReadonlyArray<SelectOption<T>>;
  selectedKey?: T | null;
  defaultSelectedKey?: T;
  onSelectionChange?: (value: T) => void;
  description?: ReactNode;
  errorMessage?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
}

/**
 * React Aria's listbox rather than a native `<select>`, so an option can carry
 * a second line — which the grant and capability pickers need, since
 * "`space.grant`" means nothing without "Grant access to others" beside it.
 */
export function Select<T extends string>({
  label,
  options,
  selectedKey,
  defaultSelectedKey,
  onSelectionChange,
  description,
  errorMessage,
  placeholder = 'Select…',
  isDisabled,
  isRequired,
  className,
}: SelectProps<T>) {
  return (
    <AriaSelect
      selectedKey={selectedKey ?? undefined}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={(key) => onSelectionChange?.(key as T)}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isInvalid={errorMessage ? true : undefined}
      className={cn('flex flex-col gap-1.5', className)}
    >
      <Label className={LABEL}>
        {label}
        {isRequired ? <span className="text-error-primary"> *</span> : null}
      </Label>
      <AriaButton
        className={cn(
          CONTROL,
          FOCUS_RING,
          'flex items-center justify-between gap-2 text-left cursor-pointer',
        )}
      >
        <SelectValue className="truncate data-placeholder:text-placeholder">
          {({ selectedText }) => selectedText ?? placeholder}
        </SelectValue>
        <span aria-hidden="true" className="text-quaternary shrink-0">
          ▾
        </span>
      </AriaButton>
      {description ? (
        <Text slot="description" className={HINT}>
          {description}
        </Text>
      ) : null}
      <FieldError className={ERROR}>{errorMessage}</FieldError>
      <Popover className="min-w-(--trigger-width) max-h-72 overflow-auto rounded-md border border-secondary bg-primary shadow-lg">
        <ListBox className="p-1 outline-none">
          {options.map((option) => (
            <ListBoxItem
              key={option.value}
              id={option.value}
              textValue={option.label}
              className={cn(
                'cursor-pointer rounded-md px-2.5 py-2 text-sm text-primary outline-none',
                'data-hovered:bg-secondary data-focused:bg-secondary data-selected:bg-brand-primary',
              )}
            >
              <span className="block font-medium">{option.label}</span>
              {option.description ? (
                <span className="block text-xs text-tertiary">{option.description}</span>
              ) : null}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
